import { useCallback, useEffect, useState } from "react";
import { Package, Envelope, Truck, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { PageHeader, EmptyState, StatCard } from "@/components/Shared";
import { COUNTRIES, countryName } from "@/lib/countries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const money = (v, c) => `${(c || "USD").toUpperCase()} ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const BLANK_SERVICE = { kind: "courier", provider: "DHL", service_name: "", origin_country: "US", scope: "domestic", item_types: "parcel", currency: "usd", min_charge: 0, band1: 0.5, price1: 0, band2: 2, price2: 0, per_extra_kg: 0, signature_fee: 0, transit_days: 3, max_weight_kg: 30, note: "" };

export default function Parcels() {
  const { user } = useAuth();
  const { home_country: homeCountry } = useCurrency();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("compare");
  const [meta, setMeta] = useState(null);
  const [services, setServices] = useState([]);
  const [kindFilter, setKindFilter] = useState("all");
  const [form, setForm] = useState({ origin_country: homeCountry || "IN", dest_country: homeCountry || "IN", weight_kg: 1, length_cm: 0, width_cm: 0, height_cm: 0, item_type: "parcel", declared_value: 0, signature: false, insure: false });
  const [result, setResult] = useState(null);
  const [svc, setSvc] = useState(BLANK_SERVICE);
  const [dialog, setDialog] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([api.get("/parcels/meta"), api.get(`/parcels/services?kind=${kindFilter}`)]);
      setMeta(m.data);
      setServices(s.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [kindFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!homeCountry) return;
    setForm((f) => ({ ...f, origin_country: homeCountry, dest_country: homeCountry }));
    setSvc((s) => ({ ...s, origin_country: homeCountry }));
  }, [homeCountry]);

  const runQuote = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/parcels/quote", {
        ...form,
        weight_kg: Number(form.weight_kg) || 0,
        length_cm: Number(form.length_cm) || 0,
        width_cm: Number(form.width_cm) || 0,
        height_cm: Number(form.height_cm) || 0,
        declared_value: Number(form.declared_value) || 0,
      });
      setResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveService = async () => {
    setBusy(true);
    try {
      await api.post("/parcels/services", {
        kind: svc.kind, provider: svc.provider, service_name: svc.service_name,
        origin_country: svc.origin_country, scope: svc.scope,
        item_types: svc.item_types === "both" ? ["document", "parcel"] : [svc.item_types],
        currency: svc.currency, min_charge: Number(svc.min_charge) || 0,        bands: [
          { up_to_kg: Number(svc.band1) || 0.5, price: Number(svc.price1) || 0 },
          { up_to_kg: Number(svc.band2) || 2, price: Number(svc.price2) || 0 },
        ].filter((b) => b.up_to_kg > 0),
        per_extra_kg: Number(svc.per_extra_kg) || 0,
        signature_fee: Number(svc.signature_fee) || 0,
        transit_days: Number(svc.transit_days) || 1,
        max_weight_kg: Number(svc.max_weight_kg) || 30,
        note: svc.note || null,
      });
      toast.success("Service added");
      setDialog(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const posts = services.filter((s) => s.kind === "post").length;
  const couriers = services.filter((s) => s.kind === "courier").length;

  return (
    <div data-testid="parcels-page">
      <PageHeader
        overline="Despatch"
        title="Courier & post"
        description="Send documents and parcels the cheap way or the fast way — national post against courier, priced on weight bands and volumetric weight."
        action={isAdmin && (
          <Button data-testid="add-service-button" onClick={() => { setSvc(BLANK_SERVICE); setDialog(true); }}>
            <Plus size={16} className="mr-1.5" /> Add a service
          </Button>
        )}
      />

      <div className="mb-5 grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-3" data-testid="parcel-stats">
        <StatCard label="Postal services" value={posts} sub="government post" icon={Envelope} tone="primary" testid="stat-posts" />
        <StatCard label="Courier services" value={couriers} sub="express carriers" icon={Truck} testid="stat-couriers" />
        <StatCard label="Posting from" value={homeCountry || "—"} sub="your home country" icon={Package} testid="stat-origin" />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4 min-w-0">
        <TabsList className="tab-strip no-scrollbar rounded-none bg-slate-100">
          <TabsTrigger value="compare" data-testid="parcel-tab-compare" className="rounded-none text-xs uppercase tracking-wider">Compare</TabsTrigger>
          <TabsTrigger value="services" data-testid="parcel-tab-services" className="rounded-none text-xs uppercase tracking-wider">Services ({services.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "compare" && (
        <div data-testid="parcel-compare">
          <div className="grid gap-3 border border-border bg-white p-5 sm:grid-cols-4">
            <div>
              <Label>Posting from</Label>
              <Select value={form.origin_country} onValueChange={(v) => setForm({ ...form, origin_country: v })}>
                <SelectTrigger data-testid="parcel-origin-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Going to</Label>
              <Select value={form.dest_country} onValueChange={(v) => setForm({ ...form, dest_country: v })}>
                <SelectTrigger data-testid="parcel-dest-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>What is it</Label>
              <Select value={form.item_type} onValueChange={(v) => setForm({ ...form, item_type: v })}>
                <SelectTrigger data-testid="parcel-item-type-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="document">Documents / letter</SelectItem>
                  <SelectItem value="parcel">Parcel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Weight (kg)</Label><Input data-testid="parcel-weight-input" type="number" min="0" step="0.1" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Length (cm)</Label><Input data-testid="parcel-length-input" type="number" min="0" value={form.length_cm} onChange={(e) => setForm({ ...form, length_cm: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Width (cm)</Label><Input data-testid="parcel-width-input" type="number" min="0" value={form.width_cm} onChange={(e) => setForm({ ...form, width_cm: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Height (cm)</Label><Input data-testid="parcel-height-input" type="number" min="0" value={form.height_cm} onChange={(e) => setForm({ ...form, height_cm: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Declared value</Label><Input data-testid="parcel-value-input" type="number" min="0" value={form.declared_value} onChange={(e) => setForm({ ...form, declared_value: e.target.value })} className="rounded-none border-slate-300" /></div>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" data-testid="parcel-signature-checkbox" checked={form.signature} onChange={(e) => setForm({ ...form, signature: e.target.checked })} /> Signature on delivery
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" data-testid="parcel-insure-checkbox" checked={form.insure} onChange={(e) => setForm({ ...form, insure: e.target.checked })} /> Insure it
            </label>
            <div className="sm:col-span-2">
              <Button data-testid="parcel-quote-button" onClick={runQuote} disabled={busy} className="w-full sm:w-auto">
                {busy ? "Comparing…" : "Compare post and courier"}
              </Button>
            </div>
          </div>

          {result && (
            <div className="mt-5" data-testid="parcel-results">
              {result.options.length === 0 ? (
                <p data-testid="parcel-no-options" className="border border-border bg-white p-5 text-sm text-slate-600">
                  Nothing covers that route and weight yet
                  {result.excluded_over_weight > 0 && ` — ${result.excluded_over_weight} service(s) were over their weight limit`}.
                  {" "}An admin can add a service, or use the freight quotes for anything heavy.
                </p>
              ) : (
                <>
                  <p className="text-sm text-slate-600" data-testid="parcel-summary">
                    {result.options.length} {result.scope} option{result.options.length > 1 ? "s" : ""} for {result.item_type === "document" ? "documents" : "a parcel"}
                    {result.cheapest_post && result.cheapest?.service_id !== result.cheapest_post.service_id &&
                      ` · cheapest post ${money(result.cheapest_post.total, result.cheapest_post.currency)} in ${result.cheapest_post.transit_days} days`}
                  </p>
                  <div className="mt-3 space-y-2">
                    {result.options.map((o, i) => (
                      <div key={o.service_id} data-testid={`parcel-option-${i}`} className={`border bg-white p-4 ${i === 0 ? "border-[#002FA7]" : "border-border"}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <div className="text-sm font-semibold text-[#0F172A]">
                              {o.provider} — {o.service_name}
                              <span className={`ml-2 border px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider ${o.kind === "post" ? "border-[#A7E3CE] bg-[#E6F7F1] text-[#0B7A5B]" : "border-slate-300 bg-slate-50 text-slate-600"}`}>
                                {o.kind === "post" ? "Government post" : "Courier"}
                              </span>
                              {i === 0 && <span className="ml-2 border border-[#002FA7] px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider text-[#002FA7]">Cheapest</span>}
                              {result.fastest?.service_id === o.service_id && <span className="ml-2 border border-slate-300 px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider text-slate-600">Fastest</span>}
                            </div>
                            <div className="text-xs text-slate-500">
                              {o.transit_days} day{o.transit_days === 1 ? "" : "s"} · {o.tracked ? "tracked" : "not tracked"} · charged on {o.chargeable_kg} kg ({o.band_label})
                              {o.volumetric_kg > 0 && ` · volumetric ${o.volumetric_kg} kg`}
                            </div>
                          </div>
                          <div className="font-display text-lg font-bold text-[#0F172A]" data-testid={`parcel-total-${i}`}>{money(o.total, o.currency)}</div>
                        </div>
                        <dl className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                          {[["Postage", o.postage], ["Extra weight", o.extra_weight_charge], ["Signature", o.signature_fee], ["Insurance", o.insurance_fee]].map(([l, v]) => (
                            <div key={l}>
                              <dt className="overline text-[0.6rem] text-slate-400">{l}</dt>
                              <dd className="text-slate-700">{money(v, o.currency)}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    ))}
                  </div>
                  {result.duty && (
                    <p className="mt-3 border-l-2 border-[#002FA7] bg-[#EEF3FF] p-3 text-xs text-[#0F172A]" data-testid="parcel-duty">
                      On arrival, expect about {money(result.duty.duty_total, result.duty.currency)} import duty
                      ({result.duty.duty_effective_pct}%) and {money(result.duty.vat_total, result.duty.currency)} VAT/GST —
                      landed {money(result.duty.landed_total, result.duty.currency)} including postage.
                    </p>
                  )}
                  <p className="mt-2 text-xs text-slate-500">{result.note}</p>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "services" && (
        <div data-testid="parcel-services">
          <div className="mb-3 sm:max-w-xs">
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger data-testid="service-kind-filter" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Post and courier</SelectItem>
                <SelectItem value="post">Government post</SelectItem>
                <SelectItem value="courier">Courier</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {services.length === 0 ? (
            <EmptyState testid="no-services" title="No services yet" description="Add the post office and courier services you actually use." />
          ) : (
            <>
            <div className="grid gap-2 md:hidden" data-testid="service-cards">
              {services.map((s) => (
                <div key={s.id} data-testid={`service-card-${s.id}`} className="border border-border bg-white p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#0F172A]">{s.provider}</div>
                      <div className="text-xs text-slate-600">{s.service_name} · {s.kind === "post" ? "Post" : "Courier"} · {s.scope}</div>
                      <div className="text-xs text-slate-500">From {countryName(s.origin_country) || s.origin_country} · {(s.item_types || []).join(", ")}</div>
                    </div>
                    {isAdmin && (
                      <button type="button" data-testid={`delete-service-card-${s.id}`} onClick={async () => {
                        if (!window.confirm(`Remove ${s.provider} ${s.service_name}?`)) return;
                        try { await api.delete(`/parcels/services/${s.id}`); toast.success("Service removed"); await load(); }
                        catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
                      }} className="shrink-0 text-xs font-semibold text-[#B3241B]">Remove</button>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 border-t border-border pt-2 text-xs">
                    <div><div className="overline text-[0.55rem] text-slate-500">Max weight</div><div className="font-mono text-slate-700">{s.max_weight_kg}kg</div></div>
                    <div><div className="overline text-[0.55rem] text-slate-500">Transit days</div><div className="font-mono text-slate-700">{s.transit_days}</div></div>
                  </div>
                  <p className="mt-2 font-mono text-xs text-slate-500">{(s.bands || []).map((b) => `${b.up_to_kg}kg ${b.price}`).join(" · ")}</p>
                </div>
              ))}
            </div>
            <div className="hidden overflow-x-auto border border-border bg-white md:block">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>{["Kind", "Provider", "Service", "From", "Scope", "Sends", "Bands", "Max", "Days", ""].map((h) => (
                    <th key={h} className="border-b border-border px-3 py-2 text-left font-semibold text-[#0F172A]">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {services.map((s) => (
                    <tr key={s.id} data-testid={`service-${s.id}`} className="border-b border-border last:border-0">
                      <td className="px-3 py-2 text-slate-600">{s.kind === "post" ? "Post" : "Courier"}</td>
                      <td className="px-3 py-2 font-medium text-[#0F172A]">{s.provider}</td>
                      <td className="px-3 py-2 text-slate-600">{s.service_name}</td>
                      <td className="px-3 py-2 text-slate-600">{countryName(s.origin_country) || s.origin_country}</td>
                      <td className="px-3 py-2 text-slate-600">{s.scope}</td>
                      <td className="px-3 py-2 text-slate-600">{(s.item_types || []).join(", ")}</td>
                      <td className="px-3 py-2 font-mono text-slate-600">{(s.bands || []).map((b) => `${b.up_to_kg}kg ${b.price}`).join(" · ")}</td>
                      <td className="px-3 py-2 font-mono text-slate-600">{s.max_weight_kg}kg</td>
                      <td className="px-3 py-2 font-mono text-slate-600">{s.transit_days}</td>
                      <td className="px-3 py-2">
                        {isAdmin && (
                          <button type="button" data-testid={`delete-service-${s.id}`} onClick={async () => {
                            if (!window.confirm(`Remove ${s.provider} ${s.service_name}?`)) return;
                            try { await api.delete(`/parcels/services/${s.id}`); toast.success("Service removed"); await load(); }
                            catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
                          }} className="text-[#B3241B] hover:underline">Remove</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add a courier or postal service</DialogTitle>
            <DialogDescription>Two weight bands and a per-extra-kg rate is enough to price most services.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Kind</Label>
              <Select value={svc.kind} onValueChange={(v) => setSvc({ ...svc, kind: v })}>
                <SelectTrigger data-testid="svc-kind-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="post">Government post</SelectItem><SelectItem value="courier">Courier</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Provider</Label>
              <Select value={svc.provider} onValueChange={(v) => setSvc({ ...svc, provider: v })}>
                <SelectTrigger data-testid="svc-provider-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{(meta?.providers || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Service name</Label><Input data-testid="svc-name-input" value={svc.service_name} onChange={(e) => setSvc({ ...svc, service_name: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div>
              <Label>Posted from</Label>
              <Select value={svc.origin_country} onValueChange={(v) => setSvc({ ...svc, origin_country: v })}>
                <SelectTrigger data-testid="svc-origin-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Scope</Label>
              <Select value={svc.scope} onValueChange={(v) => setSvc({ ...svc, scope: v })}>
                <SelectTrigger data-testid="svc-scope-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="domestic">Domestic</SelectItem><SelectItem value="international">International</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sends</Label>
              <Select value={svc.item_types} onValueChange={(v) => setSvc({ ...svc, item_types: v })}>
                <SelectTrigger data-testid="svc-items-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Documents and parcels</SelectItem>
                  <SelectItem value="document">Documents only</SelectItem>
                  <SelectItem value="parcel">Parcels only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Currency</Label><Input data-testid="svc-currency-input" value={svc.currency} onChange={(e) => setSvc({ ...svc, currency: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Band 1 up to (kg)</Label><Input data-testid="svc-band1-input" type="number" min="0" step="0.1" value={svc.band1} onChange={(e) => setSvc({ ...svc, band1: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Band 1 price</Label><Input data-testid="svc-price1-input" type="number" min="0" value={svc.price1} onChange={(e) => setSvc({ ...svc, price1: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Band 2 up to (kg)</Label><Input data-testid="svc-band2-input" type="number" min="0" step="0.1" value={svc.band2} onChange={(e) => setSvc({ ...svc, band2: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Band 2 price</Label><Input data-testid="svc-price2-input" type="number" min="0" value={svc.price2} onChange={(e) => setSvc({ ...svc, price2: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Per extra kg</Label><Input data-testid="svc-extra-input" type="number" min="0" value={svc.per_extra_kg} onChange={(e) => setSvc({ ...svc, per_extra_kg: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Signature fee</Label><Input data-testid="svc-signature-input" type="number" min="0" value={svc.signature_fee} onChange={(e) => setSvc({ ...svc, signature_fee: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Transit days</Label><Input data-testid="svc-days-input" type="number" min="0" value={svc.transit_days} onChange={(e) => setSvc({ ...svc, transit_days: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Max weight (kg)</Label><Input data-testid="svc-max-input" type="number" min="0" value={svc.max_weight_kg} onChange={(e) => setSvc({ ...svc, max_weight_kg: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div className="sm:col-span-2"><Label>Note</Label><Input data-testid="svc-note-input" value={svc.note} onChange={(e) => setSvc({ ...svc, note: e.target.value })} className="rounded-none border-slate-300" /></div>
          </div>
          <DialogFooter className="mobile-sticky-footer">
            <Button data-testid="save-service-button" onClick={saveService} disabled={busy || !svc.service_name || (Number(svc.price1) <= 0 && Number(svc.price2) <= 0 && Number(svc.min_charge) <= 0)}>Save service</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
